import { Component, OnInit, ViewChild } from '@angular/core';
import { PromptService, Prompt, AdminUser } from '../services/prompt.service';
import { NgForm } from '@angular/forms';

interface PaginatedPrompts {
  items: Prompt[];
  currentPage: number;
  pageSize: number;
  totalItems: number;
}

@Component({
  selector: 'app-prompts',
  templateUrl: './prompts.component.html',
  styleUrls: ['./prompts.component.css']
})
export class PromptsComponent implements OnInit {
  @ViewChild('promptForm') promptForm!: NgForm;

  // Expose Math for use in template
  Math = Math;

  // Prompts and pagination
  prompts: Prompt[] = [];
  paginatedPrompts: PaginatedPrompts = {
    items: [],
    currentPage: 1,
    pageSize: 100,
    totalItems: 0
  };
  lastPage: number = 1;

  // Form data
  newPromptText: string = '';
  selectedUserId: number | string = '';
  newStatus: number = 1;
  adminUsers: AdminUser[] = [];

  // Edit state
  editingId: number | null = null;

  // Filter state
  filterUserId: number | string = '';
  filterStatus: number | string = '';

  // UI state
  loading: boolean = false;
  error: string = '';
  successMessage: string = '';
  deleteConfirmId: number | null = null;

  constructor(private promptService: PromptService) {}

  ngOnInit(): void {
    this.loadAdminUsers();
    this.loadPrompts();
  }

  /**
   * Load admin users for dropdown
   */
  loadAdminUsers(): void {
    this.loading = true;
    this.promptService.getAdminUsers().subscribe({
      next: (response) => {
        if (response.success && Array.isArray(response.data)) {
          this.adminUsers = response.data as AdminUser[];
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load admin users';
        this.loading = false;
      }
    });
  }

  /**
   * Load prompts with filters and pagination
   */
  loadPrompts(page: number = 1): void {
    this.loading = true;
    this.error = '';
    this.paginatedPrompts.currentPage = page;

    // Map 'global' to null for the API
    let userId: number | string | undefined = undefined;
    if (this.filterUserId === 'global') {
      userId = 'null'; // Special value to indicate global prompts
    } else if (this.filterUserId !== '') {
      userId = this.filterUserId;
    }

    const status = this.filterStatus ? parseInt(this.filterStatus.toString()) : undefined;

    // Pass pagination parameters to service
    this.promptService.getAllPrompts(userId, status, page, this.paginatedPrompts.pageSize).subscribe({
      next: (response) => {
        if (response.success && Array.isArray(response.data)) {
          this.paginatedPrompts.items = response.data as Prompt[];
          this.paginatedPrompts.totalItems = response.count || 0;
          this.lastPage = response.lastPage || 1;
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load prompts';
        this.loading = false;
      }
    });
  }

  /**
   * Add or Update prompt
   */
  addPrompt(): void {
    if (!this.newPromptText.trim()) {
      this.error = 'Prompt text is required';
      return;
    }

    this.loading = true;
    this.error = '';
    const userId = this.selectedUserId ? parseInt(this.selectedUserId.toString()) : undefined;

    if (this.editingId) {
      // Update existing prompt
      this.promptService.updatePrompt(this.editingId, this.newPromptText, userId, this.newStatus).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = 'Prompt updated successfully';
            this.cancelEdit();
            this.loadPrompts();
            setTimeout(() => this.successMessage = '', 3000);
          } else {
            this.error = response.message || 'Failed to update prompt';
          }
          this.loading = false;
        },
        error: (err) => {
          this.error = (err.error && err.error.message) || 'Failed to update prompt';
          this.loading = false;
        }
      });
    } else {
      // Add new prompt
      this.promptService.addPrompt(this.newPromptText, userId, this.newStatus).subscribe({
        next: (response) => {
          if (response.success) {
            this.successMessage = response.message || 'Prompt added successfully';
            this.resetForm();
            this.loadPrompts();
            setTimeout(() => this.successMessage = '', 3000);
          } else {
            this.error = response.message || 'Failed to add prompt';
          }
          this.loading = false;
        },
        error: (err) => {
          this.error = (err.error && err.error.message) || 'Failed to add prompt';
          this.loading = false;
        }
      });
    }
  }

  /**
   * Start editing a prompt - load data into form
   */
  startEdit(prompt: Prompt): void {
    this.editingId = prompt.id;
    this.newPromptText = prompt.prompt_text;
    this.selectedUserId = prompt.user_id || '';
    this.newStatus = prompt.status;
  }

  /**
   * Cancel editing
   */
  cancelEdit(): void {
    this.editingId = null;
    this.resetForm();
  }

  /**
   * Delete prompt with confirmation
   */
  deletePrompt(promptId: number): void {
    this.loading = true;
    this.error = '';

    this.promptService.deletePrompt(promptId).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = 'Prompt deleted successfully';
          this.deleteConfirmId = null;
          this.loadPrompts();
          setTimeout(() => this.successMessage = '', 3000);
        } else {
          this.error = response.message || 'Failed to delete prompt';
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = (err.error && err.error.message) || 'Failed to delete prompt';
        this.loading = false;
      }
    });
  }

  /**
   * Reset form
   */
  resetForm(): void {
    this.newPromptText = '';
    this.selectedUserId = '';
    this.newStatus = 1;
    if (this.promptForm) {
      this.promptForm.resetForm();
    }
  }

  /**
   * Change page
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.lastPage) {
      this.loadPrompts(page);
    }
  }

  /**
   * Get total pages
   */
  getTotalPages(): number {
    return this.lastPage;
  }

  /**
   * Get user name by ID
   */
  getUserName(userId: number | null): string {
    if (!userId) {
      return 'Global Prompt';
    }
    const user = this.adminUsers.find(u => u.user_id === userId);
    return user ? user.user_name : `User ${userId}`;
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: number): string {
    return status === 1 ? 'badge-success' : 'badge-danger';
  }

  /**
   * Get status text
   */
  getStatusText(status: number): string {
    return status === 1 ? 'Active' : 'Inactive';
  }
}
